import { NUMBERED_REF_TOKEN_RE } from '../citationTokens';
import type { DataSource } from '../../types/siteRag';
import type { SourceRoleTruthFinding } from './sourceRoleTruthGuard';

/** Separate from the groundwater/wetland inventory and air/NEPA policies. */
export const CWA404_PERMIT_TYPE_POLICY_VERSION = 'cwa404-permit-type-v1' as const;

const INDIVIDUAL = /\b(?:CWA\s*(?:Section\s*)?404\s+|Section\s+404\s+|Clean\s+Water\s+Act\s+(?:Section\s+)?404\s+)?Individual\s+Permit\b/i;
const CWA = /\b(?:CWA(?:\s*(?:Section\s*)?404)?|Clean Water Act|Section\s*404|USACE|Army Corps|Corps of Engineers|jurisdictional\s+wetlands?|waters of the United States|dredged|wetland\s+fill)\b/i;

/** A direct qualification governs this predicate, not arbitrary earlier
 * negative/unknown prose. Unsupported complex scope remains auditable. */
function directlyQualified(preceding: string): boolean {
  const qualifier = /\b(?:not established that|unconfirmed (?:that|whether)|unknown whether|no evidence that|cannot conclude that|do not conclude that|false that|incorrect that|not true that|(?:assess|determine|confirm|evaluate) whether)\s+([^,.!?;]{0,100})$/i.exec(preceding);
  if (!qualifier) return false;
  return /^(?:(?:the|this|our|subject|a|an|proposed|planned|project|site|development|facility|plant|CWA|Section|Clean|Water|Act|404|permit)\s*){0,12}$/i.test(qualifier[1]!);
}

/** Generic regulations and wetland inventories cannot decide this site's
 * permit type. A separately cited, issued agency determination is left to
 * the ordinary citation/document-support gates, never manufactured here. */
function hasIssuedSiteDetermination(claim: string, references: readonly DataSource[]): boolean {
  const indices = [...claim.matchAll(new RegExp(NUMBERED_REF_TOKEN_RE.source, NUMBERED_REF_TOKEN_RE.flags))]
    .flatMap(token => {
      let reliability: string | undefined;
      return token[0].slice(1, -1).split(',').flatMap(part => {
        const item = /^\s*(?:(verified|inferred|unverified)\s+)?(?:Ref\s*(?::\s*)?)?(\d+)\s*$/i.exec(part);
        if (!item) return [];
        if (item[1]) reliability = item[1].toLowerCase();
        return reliability && reliability !== 'verified' ? [] : [item[2]!];
      });
    });
  return indices.some(index => {
      const ref = references[Number(index) - 1];
      if (!ref || ref.demonstrationDisclosure || ref.needsVerification
        || ref.qualification !== 'verified' || ref.sourceGrade !== 'authoritative-government') return false;
      const metadata = `${ref.field} ${ref.source} ${ref.url ?? ''}`;
      return /\b(?:site[- ]specific|project[- ]specific)\b/i.test(metadata)
        && /\b(?:issued|executed)\b/i.test(metadata)
        && /\b(?:determination|permit)\b/i.test(metadata)
        && /\bIndividual\s+Permit\b/i.test(metadata) && CWA.test(metadata)
        && /https?:\/\/[^/]+\.(?:gov|mil)(?:\/|$)/i.test(ref.url ?? '')
        && !/\b(?:generic|guidance|inventory|screening|overview|proposal|developer|AI|research)\b/i.test(metadata);
    });
}

/** D04's standalone unsupported Individual Permit determination rule.
 * It does not decide jurisdiction, impacts, exemptions or general-permit
 * eligibility. Those remain scoped agency workstreams. */
export function cwa404PermitTypeFindings(
  text: string, sectionId: string, references: readonly DataSource[] = [],
): SourceRoleTruthFinding[] {
  const findings: SourceRoleTruthFinding[] = [];
  // A soft line wrap does not start a new assertion or remove its governing
  // may/denial. Keep explicit CWA context across clauses of the same
  // sentence, but evaluate qualifications independently after a semicolon.
  // Sentence punctuation and blank paragraphs remain context boundaries.
  const clauses = text.split(/(?<=[.!?])(?<!\bU\.S\.)\s+|\n\s*\n/)
    .flatMap(sentence => sentence.split(/;\s*|,?\s+(?:but|whereas|however)\s+/i)
      .map(claim => ({ claim, cwaContext: CWA.test(sentence) })));
  for (const { claim, cwaContext } of clauses) {
    if (!INDIVIDUAL.test(claim) || !(cwaContext
      || /(?:environmental|ecological|wetland|permitting|nepa)/i.test(sectionId))) continue;
    if (/\b(?:NPDES|Section\s*402|Clean\s+Air\s+Act)\b/i.test(claim)
      && !/\b(?:Section\s*404|CWA\s*404|Clean\s+Water\s+Act\s+(?:Section\s*)?404)\b/i.test(claim)) continue;
    if (/\b(?:another|other|different)\s+(?:site|project|facility)\b/i.test(claim)
      && !/\b(?:this|our|subject)\s+(?:site|project|facility)\b/i.test(claim)) continue;
    const required = /\b(?:will\s+(?:likely\s+)?require|requires?|must\s+(?:obtain|secure)|requirement\s+for)\b[^.!?;]{0,90}\bIndividual\s+Permit\b/i.exec(claim)
      ?? /\b(?:cannot|can['’]t|may\s+not)\s+proceed\b[^.!?;]{0,90}\buntil\b[^.!?;]{0,90}\bIndividual\s+Permit\b[^.!?;]{0,60}\b(?:issued|obtained|secured)\b/i.exec(claim)
      ?? /\bIndividual\s+Permit\b[^.!?;]{0,45}\b(?:is|will\s+be)\s+(?:required|mandatory|necessary)\b/i.exec(claim);
    if (!required) continue;
    const preceding = claim.slice(0, required.index);
    // A direct denial/question is not an adopted determination. Qualifiers
    // elsewhere do not clear a later, separate affirmative requirement.
    if (claim.trim().endsWith('?')
      || /\b(?:may|might|could|potentially|possibly)\s*$/i.test(preceding)
      || /\b(?:(?:does?|will|may|might|could)\s+not|No)\s*$/i.test(preceding)
      || directlyQualified(preceding)
      || /^\s*Whether\b[^;.!?]*\bIndividual\s+Permit\b[^;.!?]*\b(?:is|remains)\s+(?:unknown|unconfirmed|undetermined)\s*[.]?\s*$/i.test(claim)) continue;
    // Only conditioning on this permit-type determination is adequate. "If
    // jurisdictional wetlands exist, an Individual Permit is required" still
    // invents the individual-vs-general pathway and must remain blocked.
    if (/^\s*If\s+(?:an?\s+)?(?:CWA\s*(?:Section\s*)?404\s+|Section\s+404\s+)?Individual\s+Permit\s+is\s+required\s*,/i.test(claim)
      || /^\s*If\s+(?:USACE|the\s+(?:Corps|permitting\s+agency|agency))\s+determines?\s+(?:that\s+)?[^,;]{0,90}\bIndividual\s+Permit\s+(?:is\s+)?required\s*,/i.test(claim)) continue;
    if (hasIssuedSiteDetermination(claim, references)) continue;
    const expected = 'Section 404 jurisdiction and authorization depend on the proposed dredged/fill discharge, applicable exemptions and agency determination. A wetland inventory or screening does not determine an Individual Permit; assess individual versus applicable general-permit pathways with USACE or the approved state authority.';
    findings.push({ kind: 'unverified-cwa404-permit-determination', severity: 'block', sectionId,
      policyVersion: CWA404_PERMIT_TYPE_POLICY_VERSION,
      excerpt: claim.trim(), observed: claim.trim(), expected, message: expected,
      sourcePaths: ['environmental.data.wetlandAcreageBasis', 'references'] });
  }
  return findings;
}
